import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { 
  Save as SaveIcon,
  Send as SendIcon,
  PictureAsPdf as PdfIcon,
  ExpandMore as ExpandMoreIcon,
  Print as PrintIcon,
  Preview as PreviewIcon,
  Assignment as AssignmentIcon,
  Comment as CommentIcon,
  FindInPage as FindingIcon,
  BugReport as DeficiencyIcon,
  Healing as RemediationIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { remapReviews } from '../../../helpers/utils';

const ReportGenerator = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [auditCase, setAuditCase] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState({
    content: '',
    format: 'standard'
  });
  const [existingReport, setExistingReport] = useState(null);
  const [pdfGenerated, setPdfGenerated] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const quillRef = useRef(null);

  // Report format options
  const reportFormats = [
    { value: 'standard', label: 'Standard Report' },
    { value: 'detailed', label: 'Detailed Report' },
    { value: 'executive', label: 'Executive Summary' }
  ];

  // Workflow steps
  const steps = [
    'Review Summary',
    'Generate Report',
    'Finalize & Send'
  ];

  // Quill editor modules and formats
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'color', 'background',
    'link', 'image'
  ];

  // Fetch case, reviews, and existing report on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch case details
        const caseResponse = await axios.get(`${process.env.REACT_APP_API_URL}/audit/cases/${caseId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        setAuditCase(caseResponse.data);
        
        
        // Fetch reviews for this case
        const reviewsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/audit/reviews/${caseId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });

        console.log(reviewsResponse);

        setReviews(remapReviews(reviewsResponse));
        
        // Fetch existing report if any
        const reportsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/audit/reports?case_id=${caseId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (reportsResponse.data.length > 0) {
          const existingReport = reportsResponse.data[0];
          setExistingReport(existingReport);
          setReport({
            content: existingReport.content,
            format: 'standard' // Default format
          });
          setPdfGenerated(existingReport.pdf_path !== null);
        } else {
          // Initialize with template if no existing report
          setReport({
            content: generateReportTemplate(caseResponse.data, reviewsResponse.data),
            format: 'standard'
          });
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load report data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [caseId]);

  // Generate report template based on case and reviews
  const generateReportTemplate = (auditCase, reviews) => {
    if (!auditCase || !reviews.length) return '';
    
    const firmName = auditCase.firm?.name || 'Client';
    const reviewPeriodStart = new Date(auditCase.review_period_start).toLocaleDateString();
    const reviewPeriodEnd = new Date(auditCase.review_period_end).toLocaleDateString();
    
    // Count deficiencies by severity
    const deficiencies = {
      high: reviews.filter(r => r.has_deficiency && r.deficiency_severity === 'high').length,
      medium: reviews.filter(r => r.has_deficiency && r.deficiency_severity === 'medium').length,
      low: reviews.filter(r => r.has_deficiency && r.deficiency_severity === 'low').length
    };
    
    // Generate template
    return `
      <h1>Audit Report: ${firmName}</h1>
      <p><strong>Review Period:</strong> ${reviewPeriodStart} to ${reviewPeriodEnd}</p>
      
      <h2>Executive Summary</h2>
      <p>This report presents the findings of our audit of ${firmName} for the period ${reviewPeriodStart} to ${reviewPeriodEnd}. The audit was conducted in accordance with professional standards and practices.</p>
      
      <h2>Deficiency Summary</h2>
      <ul>
        <li><strong>High Severity:</strong> ${deficiencies.high}</li>
        <li><strong>Medium Severity:</strong> ${deficiencies.medium}</li>
        <li><strong>Low Severity:</strong> ${deficiencies.low}</li>
      </ul>
      
      <h2>Key Findings</h2>
      <p>[Insert key findings here]</p>
      
      <h2>Recommendations</h2>
      <p>[Insert recommendations here]</p>
      
      <h2>Conclusion</h2>
      <p>[Insert conclusion here]</p>
    `;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReport(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle rich text editor changes
  const handleEditorChange = (content) => {
    setReport(prev => ({
      ...prev,
      content
    }));
  };

  // Handle step navigation
  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  // Save the current report
  const saveReport = async () => {
    try {
      const reportData = {
        case_id: caseId,
        content: report.content
      };
      
      let response;
      
      if (existingReport) {
        response = await axios.put(`${process.env.REACT_APP_API_URL}/audit/reports/${existingReport.id}`, reportData, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      } else {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/audit/reports`, reportData, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        setExistingReport(response.data);
      }
      
      setSnackbar({
        open: true,
        message: 'Report saved successfully',
        severity: 'success'
      });
      
      return true;
    } catch (err) {
      console.error('Error saving report:', err);
      setSnackbar({
        open: true,
        message: 'Error saving report. Please try again.',
        severity: 'error'
      });
      
      return false;
    }
  };

  // Generate PDF from report
  const generatePdf = async () => {
    // First save the current report
    const saveResult = await saveReport();
    
    if (!saveResult) return;
    
    try {
      setSnackbar({
        open: true,
        message: 'Generating PDF, please wait...',
        severity: 'info'
      });
      
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/audit/cases/${caseId}/report/generate-pdf`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      setPdfGenerated(true);
      
      setSnackbar({
        open: true,
        message: 'PDF generated successfully',
        severity: 'success'
      });
      
      // Refresh the report data without reloading the entire page
      setTimeout(async () => {
        try {
          // Fetch the updated report
          const reportsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/audit/reports?case_id=${caseId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          
          if (reportsResponse.data.length > 0) {
            const updatedReport = reportsResponse.data[0];
            setExistingReport(updatedReport);
            setReport({
              content: updatedReport.content,
              format: 'standard'
            });
          }
        } catch (error) {
          console.error('Error refreshing report data:', error);
        }
      }, 1500); // Give the user time to see the success message
    } catch (err) {
      console.error('Error generating PDF:', err);
      setSnackbar({
        open: true,
        message: 'Error generating PDF. Please try again.',
        severity: 'error'
      });
    }
  };

  // Download PDF report
  const downloadPdf = () => {
    if (!existingReport || !existingReport.pdf_path) {
      setSnackbar({
        open: true,
        message: 'No PDF available. Please generate a PDF first.',
        severity: 'warning'
      });
      return;
    }
    
    window.open(`${process.env.REACT_APP_API_URL}/audit/reports/${existingReport.id}/download`, '_blank');
  };

  // Preview PDF report
  const previewPdf = () => {
    if (!existingReport || !existingReport.pdf_path) {
      setSnackbar({
        open: true,
        message: 'No PDF available. Please generate a PDF first.',
        severity: 'warning'
      });
      return;
    }
    
    // Open the preview in a new tab
    window.open(`${process.env.REACT_APP_API_URL}/audit/reports/${existingReport.id}/preview`, '_blank');
  };

  // Print report
  const printReport = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Audit Report: ${auditCase?.firm?.name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .report-content { max-width: 800px; margin: 0 auto; }
          </style>
        </head>
        <body>
          <div class="report-content">
            ${report.content}
          </div>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Handle opening the send report confirmation dialog
  const handleSendReport = () => {
    if (!pdfGenerated) {
      setSnackbar({
        open: true,
        message: 'Please generate a PDF before sending the report',
        severity: 'warning'
      });
      return;
    }
    
    setConfirmDialog({
      open: true,
      title: 'Finalize and Send Report',
      message: `Are you sure you want to send this report to ${auditCase?.firm?.name}? This will finalize the case and mark it as completed. This action cannot be undone.`,
      onConfirm: sendReportToClient
    });
  };
  
  // Send report to client
  const sendReportToClient = async () => {
    try {
      setSnackbar({
        open: true,
        message: 'Sending report to client...',
        severity: 'info'
      });
      
      await axios.post(`${process.env.REACT_APP_API_URL}/audit/cases/${caseId}/report/send`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      setSnackbar({
        open: true,
        message: 'Report sent to client successfully! The case has been finalized.',
        severity: 'success'
      });
      
      // Navigate back to the case list after a delay
      setTimeout(() => {
        navigate('/audit/cases');
      }, 3000);
    } catch (err) {
      console.error('Error sending report:', err);
      setSnackbar({
        open: true,
        message: `Error sending report: ${err.response?.data?.message || 'Please try again.'}`,
        severity: 'error'
      });
    }
  };
  
  // Handle dialog close
  const handleCloseDialog = () => {
    setConfirmDialog(prev => ({
      ...prev,
      open: false
    }));
  };
  
  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  // Render review summary step
  const renderReviewSummary = () => {
    if (!auditCase || !reviews.length) {
      return <Typography>No reviews available</Typography>;
    }
    
    // Group reviews by type (first, second)
    const firstReviews = reviews.filter(r => r.review_type === 'first');
    const secondReviews = reviews.filter(r => r.review_type === 'second');
    
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Review Summary
        </Typography>
        
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              <FindingIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Findings Summary
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Deficiencies by Severity
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', my: 2 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Chip 
                          label={reviews.filter(r => r.has_deficiency && r.deficiency_severity === 'high').length} 
                          color="error" 
                          sx={{ fontSize: '1.2rem', width: 60, height: 60 }}
                        />
                        <Typography variant="body2" sx={{ mt: 1 }}>High</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Chip 
                          label={reviews.filter(r => r.has_deficiency && r.deficiency_severity === 'medium').length} 
                          color="warning" 
                          sx={{ fontSize: '1.2rem', width: 60, height: 60 }}
                        />
                        <Typography variant="body2" sx={{ mt: 1 }}>Medium</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Chip 
                          label={reviews.filter(r => r.has_deficiency && r.deficiency_severity === 'low').length} 
                          color="info" 
                          sx={{ fontSize: '1.2rem', width: 60, height: 60 }}
                        />
                        <Typography variant="body2" sx={{ mt: 1 }}>Low</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Engagement Related Issues
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', my: 2 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Chip 
                          label={reviews.filter(r => r.relates_to_engagements).length} 
                          color="primary" 
                          sx={{ fontSize: '1.2rem', width: 60, height: 60 }}
                        />
                        <Typography variant="body2" sx={{ mt: 1 }}>Engagement Issues</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Chip 
                          label={reviews.filter(r => r.has_deficiency).length} 
                          color="secondary" 
                          sx={{ fontSize: '1.2rem', width: 60, height: 60 }}
                        />
                        <Typography variant="body2" sx={{ mt: 1 }}>Total Deficiencies</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              <CommentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              First Review Comments
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {firstReviews.length === 0 ? (
              <Typography variant="body2">No first review comments available</Typography>
            ) : (
              firstReviews.map(review => (
                <Paper key={review.id} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Question: {review.answer?.question_text || 'Unknown Question'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Comment:</strong> {review.comment || 'No comment'}
                  </Typography>
                  {review.has_deficiency && (
                    <Box sx={{ mt: 1 }}>
                      <Chip 
                        label={`${review.deficiency_severity.toUpperCase()} Deficiency`} 
                        color={
                          review.deficiency_severity === 'high' ? 'error' : 
                          review.deficiency_severity === 'medium' ? 'warning' : 'info'
                        }
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {review.relates_to_engagements && (
                        <Chip 
                          label="Engagement Related" 
                          color="primary"
                          size="small"
                        />
                      )}
                    </Box>
                  )}
                </Paper>
              ))
            )}
          </AccordionDetails>
        </Accordion>
        
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              <CommentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Second Review Comments
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {secondReviews.length === 0 ? (
              <Typography variant="body2">No second review comments available</Typography>
            ) : (
              secondReviews.map(review => (
                <Paper key={review.id} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Question: {review.answer?.question_text || 'Unknown Question'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Comment:</strong> {review.comment || 'No comment'}
                  </Typography>
                  {review.has_deficiency && (
                    <Box sx={{ mt: 1 }}>
                      <Chip 
                        label={`${review.deficiency_severity.toUpperCase()} Deficiency`} 
                        color={
                          review.deficiency_severity === 'high' ? 'error' : 
                          review.deficiency_severity === 'medium' ? 'warning' : 'info'
                        }
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {review.relates_to_engagements && (
                        <Chip 
                          label="Engagement Related" 
                          color="primary"
                          size="small"
                        />
                      )}
                    </Box>
                  )}
                </Paper>
              ))
            )}
          </AccordionDetails>
        </Accordion>
        
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              <RemediationIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Remedial Actions
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {reviews.filter(r => r.remedial_action).length === 0 ? (
              <Typography variant="body2">No remedial actions specified</Typography>
            ) : (
              reviews.filter(r => r.remedial_action).map(review => (
                <Paper key={review.id} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Question: {review.answer?.question_text || 'Unknown Question'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Remedial Action:</strong> {review.remedial_action}
                  </Typography>
                </Paper>
              ))
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  // Render report editor step
  const renderReportEditor = () => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Report Editor
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Report Format</InputLabel>
          <Select
            name="format"
            value={report.format}
            onChange={handleInputChange}
            label="Report Format"
          >
            {reportFormats.map(format => (
              <MenuItem key={format.value} value={format.value}>
                {format.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Box sx={{ mb: 3 }}>
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={report.content}
            onChange={handleEditorChange}
            modules={modules}
            formats={formats}
            style={{ height: '400px', marginBottom: '50px' }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<SaveIcon />}
            onClick={saveReport}
          >
            Save Draft
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<PrintIcon />}
            onClick={printReport}
          >
            Print Preview
          </Button>
        </Box>
      </Box>
    );
  };

  // Render finalize step
  const renderFinalize = () => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Finalize Report
        </Typography>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Report Status
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Draft Saved:</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {existingReport ? (
                <Chip 
                  label="Saved" 
                  color="success" 
                  size="small" 
                />
              ) : (
                <Chip 
                  label="Not Saved" 
                  color="error" 
                  size="small" 
                />
              )}
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>PDF Generated:</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {pdfGenerated ? (
                <Chip 
                  label="Generated" 
                  color="success" 
                  size="small" 
                />
              ) : (
                <Chip 
                  label="Not Generated" 
                  color="error" 
                  size="small" 
                />
              )}
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Sent to Client:</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {existingReport && existingReport.sent_at ? (
                <Chip 
                  label="Sent" 
                  color="success" 
                  size="small" 
                />
              ) : (
                <Chip 
                  label="Not Sent" 
                  color="error" 
                  size="small" 
                />
              )}
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" gutterBottom>
            Recipient Information
          </Typography>
          
          <Typography variant="body2" gutterBottom>
            <strong>Firm:</strong> {auditCase?.firm?.name}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Contact Email:</strong> {auditCase?.firm?.contact_email}
          </Typography>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<PdfIcon />}
              onClick={generatePdf}
              disabled={!existingReport}
            >
              Generate PDF
            </Button>
            
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<PreviewIcon />}
              onClick={previewPdf}
              disabled={!pdfGenerated}
            >
              Preview PDF
            </Button>
            
            <Button 
              variant="outlined" 
              color="secondary" 
              startIcon={<DownloadIcon />}
              onClick={downloadPdf}
              disabled={!pdfGenerated}
            >
              Download PDF
            </Button>
            
            <Button 
              variant="contained" 
              color="success" 
              startIcon={<SendIcon />}
              onClick={handleSendReport}
              disabled={!pdfGenerated || (existingReport && existingReport.sent_at)}
            >
              Send Report to Client
            </Button>
          </Box>
        </Paper>
        
        {existingReport && existingReport.sent_at && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This report was sent to the client on {new Date(existingReport.sent_at).toLocaleString()}.
          </Alert>
        )}
      </Box>
    );
  };

  // Render current step content
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderReviewSummary();
      case 1:
        return renderReportEditor();
      case 2:
        return renderFinalize();
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h1">
              <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Report Generator: {auditCase?.title}
            </Typography>
            <Chip 
              label={auditCase?.status.replace('_', ' ').toUpperCase()} 
              color="primary" 
            />
          </Box>
          
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          <Paper sx={{ p: 3, mb: 3 }}>
            {getStepContent(activeStep)}
          </Paper>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={activeStep === steps.length - 1}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={() => {
              confirmDialog.onConfirm();
              handleCloseDialog();
            }} 
            color="primary" 
            variant="contained" 
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportGenerator;
